import { Link } from 'react-router-dom'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useLotHolds } from '@/hooks/useInventoryMaster'
import type { ApiLotHold } from '@/api/inventoryApi'

/** Which claims to show — the column the number was clicked in. */
export type HoldCategory = 'in_use' | 'all'

const TITLE: Record<HoldCategory, string> = {
  in_use: 'In use by started steps',
  all: 'Who has reserved this lot',
}

/**
 * `started` is decided server-side from the STEP'S status, so this filter draws
 * exactly the line the In Use column draws — the rows always total the figure
 * that opened the dialog.
 */
function matches(hold: ApiLotHold, category: HoldCategory): boolean {
  return category === 'all' ? true : hold.started
}

function formatWhen(iso: string): string {
  const d = new Date(iso)
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
}

/**
 * Who reserved a lot, when, how much, and against what.
 *
 * Opened from the Reserved / In Use figures, and filtered to whichever was
 * clicked so the list always totals the number that opened it.
 */
export function LotHoldsDialog({
  lotId,
  lotNumber,
  category,
  onClose,
}: {
  lotId: string | null
  lotNumber: string
  category: HoldCategory
  onClose: () => void
}) {
  const { data, isLoading, isError } = useLotHolds(lotId, lotId !== null)
  const holds = (data ?? []).filter((h) => matches(h, category))
  const total = holds.reduce((sum, h) => sum + h.qty, 0)

  return (
    <Dialog open={lotId !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='sm:max-w-2xl'>
        <DialogHeader>
          <DialogTitle>{TITLE[category]}</DialogTitle>
          <DialogDescription>
            Lot {lotNumber}
            {!isLoading && !isError && holds.length > 0 && ` — ${total} held across ${holds.length} ${holds.length === 1 ? 'claim' : 'claims'}`}
          </DialogDescription>
        </DialogHeader>

        {isLoading && <p className='py-6 text-sm text-muted-foreground'>Loading…</p>}

        {isError && (
          <p className='py-6 text-sm text-destructive'>Could not load what is holding this lot.</p>
        )}

        {!isLoading && !isError && holds.length === 0 && (
          <p className='py-6 text-sm text-muted-foreground'>Nothing is holding this lot.</p>
        )}

        {!isLoading && !isError && holds.length > 0 && (
          <div className='overflow-x-persistent'>
            <table className='w-full text-sm'>
              <thead>
                <tr className='border-b text-left text-xs uppercase tracking-wide text-muted-foreground'>
                  <th className='py-2 pr-3 text-right'>Qty</th>
                  <th className='py-2 pr-3'>Patient</th>
                  <th className='py-2 pr-3'>Against</th>
                  <th className='py-2 pr-3'>Step</th>
                  <th className='py-2'>Held since</th>
                </tr>
              </thead>
              <tbody>
                {holds.map((h) => (
                  <tr
                    key={`${h.submissionId}:${h.fieldId}:${h.requirementId}`}
                    className='border-b border-gray-100 last:border-0'
                  >
                    <td className='py-2 pr-3 text-right font-medium tabular-nums'>{h.qty}</td>
                    <td className='py-2 pr-3'>
                      {h.patientMrn ? (
                        <Link
                          to={`/patients/${encodeURIComponent(h.patientMrn)}`}
                          className='font-medium text-blue-700 hover:underline'
                        >
                          {h.patientMrn}
                        </Link>
                      ) : (
                        // The hold outlived its submission. It is still holding
                        // stock, so it is named rather than hidden — otherwise
                        // the rows would not sum to the figure above.
                        <span className='italic text-gray-400'>submission removed</span>
                      )}
                    </td>
                    <td className='py-2 pr-3 text-gray-600'>{h.formType ?? '—'}</td>
                    <td className='py-2 pr-3 text-gray-600'>
                      {h.stepStatus ?? h.submissionStatus ?? '—'}
                    </td>
                    <td className='py-2 whitespace-nowrap text-gray-500'>
                      {formatWhen(h.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
